import type { PaneData, PaneTable } from '../../shared/contracts';
import { formatDecimal, formatMetric, formatTableCell } from '../../shared/format';
import { PaneFrame } from './PaneFrame';
import { Delta } from './Delta';
import { BarChart, AreaChart, DonutChart, RadialRing, StackedBarChart } from './charts';

/**
 * Chart-less metric panes with a drill table (e.g. Prognose) preview their largest
 * rows instead of leaving the card half-empty — the drill still holds the full table.
 */
function TablePreview({ table }: { table: PaneTable }) {
  const firstCol = table.columns[0];
  const valueCol = table.columns.find((c) => c.format === 'currency') ?? table.columns[1];
  if (!firstCol || !valueCol) return null;
  const top = [...table.rows]
    .sort((a, b) => {
      const av = a.cells[valueCol.key];
      const bv = b.cells[valueCol.key];
      return (typeof bv === 'number' ? bv : 0) - (typeof av === 'number' ? av : 0);
    })
    .slice(0, 4);
  if (top.length === 0) return null;
  return (
    <div>
      <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-brand-dark/45">Største åbne</p>
      <ul className="divide-y divide-brand-primary/5">
        {top.map((r) => (
          <li key={r.id} className="flex items-center gap-3 py-1.5">
            <span className="min-w-0 flex-1 truncate text-sm text-brand-dark">{formatTableCell(r.cells[firstCol.key] ?? null, firstCol.format)}</span>
            <span className="whitespace-nowrap text-sm font-semibold text-brand-primary tabular-nums">
              {formatTableCell(r.cells[valueCol.key] ?? null, valueCol.format)}
            </span>
          </li>
        ))}
      </ul>
      {table.rows.length > top.length && (
        <p className="mt-1.5 text-[11px] text-brand-dark/45">…og {table.rows.length - top.length} flere i datagrundlaget</p>
      )}
    </div>
  );
}

export function MetricCard({ pane, wide, onDrill, eyebrow }: { pane: PaneData; wide?: boolean; onDrill?: (bucket?: string) => void; eyebrow?: string }) {
  const metric = pane.metric;
  if (!metric) return null;

  // Only advertise a click when the drill modal has something to show (a chart category,
  // a multi-point trend, or underlying rows) — otherwise the card stays non-interactive
  // instead of opening an empty "Ingen rækker" modal.
  const drillable = Boolean(
    (metric.trend && metric.trend.length > 1) ||
      pane.breakdown?.length ||
      pane.stacked?.series.length ||
      pane.details?.length ||
      pane.rows?.length ||
      pane.table?.rows.length,
  );
  const onOpen = drillable ? () => onDrill?.() : undefined;

  if (metric.max != null) {
    // The ratings behind the average (details carry the per-ticket score) give the
    // gauge context: a score distribution + the sample size, not just a bare ring.
    const scores = (pane.details ?? [])
      .map((d) => Number(String(d.value ?? '').replace(',', '.')))
      .filter((v) => Number.isFinite(v) && v > 0);
    const dist = Array.from({ length: metric.max }, (_, i) => {
      const score = metric.max! - i;
      return { score, count: scores.filter((v) => Math.round(v) === score).length };
    });
    const maxCount = Math.max(1, ...dist.map((d) => d.count));
    return (
      <PaneFrame title={pane.title} source={pane.source} eyebrow={eyebrow} periodLabel={pane.periodLabel} onOpen={onOpen}>
        {/* Centered + sized to the card so the gauge owns the space instead of hugging a corner. */}
        <div className="flex h-full min-h-40 flex-col items-center justify-center gap-3">
          {/* flex-wrap: in a narrow one-column card the distribution stacks under the
              ring instead of clipping; /70 text keeps WCAG AA contrast at this size. */}
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3">
            <RadialRing value={metric.value} max={metric.max} centerLabel={formatDecimal(metric.value)} sublabel={`af ${metric.max}`} className="h-32 w-32" />
            {scores.length > 0 && (
              <div className="w-36 space-y-1">
                {dist.map((d) => (
                  <div key={d.score} className="flex items-center gap-2 text-[11px] text-brand-dark/70">
                    <span className="w-3 text-right tabular-nums">{d.score}</span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-brand-primary/10">
                      <div className="h-full rounded-full bg-brand-primary" style={{ width: `${(d.count / maxCount) * 100}%` }} />
                    </div>
                    <span className="w-5 tabular-nums">{d.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {scores.length > 0 && (
            <p className="text-xs text-brand-dark/70">
              Baseret på {scores.length} {scores.length === 1 ? 'vurdering' : 'vurderinger'}
            </p>
          )}
          <Delta ratio={metric.deltaPct} />
          {pane.note && <p className="text-center text-xs text-brand-dark/50">{pane.note}</p>}
        </div>
      </PaneFrame>
    );
  }

  const hasChart = Boolean(
    pane.stacked?.series.length || pane.breakdown?.length || (metric.trend && metric.trend.length > 1),
  );

  return (
    <PaneFrame title={pane.title} source={pane.source} eyebrow={eyebrow} periodLabel={pane.periodLabel} onOpen={onOpen}>
      <div className="flex items-end justify-between gap-3">
        <div className="font-heading text-4xl font-bold tracking-tight text-brand-primary">
          {formatMetric(metric.value, metric.format, metric.unit)}
        </div>
        <Delta ratio={metric.deltaPct} />
      </div>
      {hasChart ? (
        <div className="mt-4" onClick={(e) => e.stopPropagation()}>
          {pane.stacked?.series.length ? (
            <StackedBarChart data={pane.stacked} onSelect={(b) => onDrill?.(b)} />
          ) : pane.breakdown?.length ? (
            <DonutChart data={pane.breakdown} onSelect={(b) => onDrill?.(b)} legendRight={wide} />
          ) : wide ? (
            <AreaChart data={metric.trend!} onSelect={(b) => onDrill?.(b)} />
          ) : (
            <BarChart data={metric.trend!} onSelect={(b) => onDrill?.(b)} />
          )}
        </div>
      ) : pane.table?.rows.length ? (
        // No stopPropagation: a click on the preview opens the drill like the card does.
        <div className="mt-4">
          <TablePreview table={pane.table} />
        </div>
      ) : null}
      {pane.note && <p className="mt-3 text-xs text-brand-dark/50">{pane.note}</p>}
    </PaneFrame>
  );
}
